import useProxiesInPool from '@/hooks/queries/useProxiesInPool';
import Card from '../ui/Card';
import { useToast } from '@/hooks/useToast';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { LucideCopy } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/Tooltip';
import { TestProxyButton } from '@/blocks/TestSingleProxyStatus';
import usePoolById from '@/hooks/queries/usePoolById';
import Flex from '../ui/Flex';
import CopySingleProxyButton from './CopySingleProxyButton';

export default function ProxiesTable({ poolId }: { poolId: number }) {
  const { data: proxies, isHideCredential } = useProxiesInPool(poolId);
  const { data: pool } = usePoolById(poolId);

  return (
    <Card className="bg-primary-background p-5 text-secondary">
      <table className="w-full">
        <thead className="text-xs font-semibold pb-3">
          <tr>
            <th className="text-left pb-3">Location</th>
            <th className="text-left pb-3">Host</th>
            <th className="text-left pb-3">Port</th>
            <th className="text-left pb-3">Username</th>
            <th className="text-left pb-3">Password</th>
            <th className="text-left pb-3 w-[400px]"></th>
          </tr>
        </thead>
        <tbody>
          {proxies?.map((proxy) => (
            <tr key={proxy.username + proxy.password}>
              <Td>{proxy.countryCode}</Td>
              <Td>
                <CopyField value={proxy.host} />
              </Td>
              <Td>
                <CopyField value={proxy.port.toString()} />
              </Td>
              <Td>
                {isHideCredential ? (
                  <HiddenField value={proxy.username} />
                ) : (
                  <CopyField value={proxy.username} />
                )}
              </Td>
              <Td>
                {isHideCredential ? (
                  <HiddenField value={proxy.password} />
                ) : (
                  <CopyField value={proxy.password} />
                )}
              </Td>
              <Td>
                <Flex.Row className="justify-end gap-3 w-full">
                  <TestProxyButton
                    proxy={proxy}
                    protocol={pool?.protocol ?? 'http'}
                  />
                  <CopySingleProxyButton proxy={proxy} disabled={isHideCredential} />
                </Flex.Row>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="py-3 border-b border-proxy-border text-sm">{children}</td>
  );
}

function CopyField({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const { toast } = useToast();

  function handleClick() {
    copyToClipboard(value);
    toast({
      description: (
        <div className="flex items-center gap-2">
          <LucideCopy className="inline-block h-4 -mt-[1.5px] stroke-indigo-600" />
          Copied to clipboard
        </div>
      ),
    });
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div
            className={cn(
              'flex items-center gap-1 hover:cursor-pointer hover:opacity-70',
              className
            )}
            onClick={handleClick}
          >
            <span>{value}</span>
            <LucideCopy className={cn('inline-block h-4', className)} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Click to copy</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function HiddenField({ value }: { value: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div
            className={cn(
              'flex items-center gap-1 hover:cursor-pointer hover:opacity-70'
            )}
          >
            <span>{value}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Purchase traffic to see credentials</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
